import { memo, useCallback, type FC } from "react";
import type { CompanyFilters, Lookups } from "../types";
import type { FilterModule } from "./index";
import { useCompanyFilterSlice, useCompanyFilterActions } from "../hooks";
import { GeographySelector } from "./geography/GeographyFilter";
import { getChips as getGeographyChips } from "./geography/chips";
import { defaultGeographySlice, type GeographySlice } from "./geography/slice";

// Countries only, as OppGen's Contact Location (GeographyFilterContent
// countryOnly). Hermes's contact location filter has no state ids, and its
// region names are contact regions rather than AMER/EMEA/APAC, so the
// company Geography filter's other scopes would be dropped or match nothing.
type Slice = GeographySlice;
const defaultSlice: Slice = { ...defaultGeographySlice, approach: "countries" };

const ContactLocationFilter: FC = memo(() => {
  const slice = useCompanyFilterSlice("contactLocation");
  const { setSlice } = useCompanyFilterActions();
  const handleChange = useCallback(
    (next: Slice) => setSlice("contactLocation", next),
    [setSlice]
  );

  return (
    <GeographySelector
      value={slice}
      onChange={handleChange}
      idPrefix="contact-location"
      countryOnly
    />
  );
});

ContactLocationFilter.displayName = "ContactLocationFilter";

export const contactLocationModule: FilterModule<Slice, Lookups> = {
  key: "contactLocation",
  defaultSlice,
  Component: ContactLocationFilter,
  toCompanyFilters: (slice): Partial<CompanyFilters> =>
    slice.countries.length > 0
      ? { contactFilters: { location: { countryAlpha2s: slice.countries } } }
      : {},
  fromCompanyFilters: (filters): Slice => ({
    ...defaultSlice,
    countries: (filters.contactFilters?.location?.countryAlpha2s ?? []).filter(
      (code): code is string => code != null
    ),
  }),
  getChips: (slice, setSlice, lookups) =>
    getGeographyChips(slice, setSlice, lookups).map((chip) => ({
      ...chip,
      id: `contacts-location-${chip.id}`,
      label: `Contact ${chip.label}`,
      filterKey: "contactLocation" as const,
    })),
  getCount: (slice) => slice.countries.length,
};
